/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import { mapResolvedDocument } from '../../studio/utils/promptApiMappers';
import { buildCreatePromptPayload, buildFullPromptUpdatePayload } from '../../studio/utils/promptSaveUtils';
import { colors } from '../../studio/constants/theme';
import {
  HISTORY_DIFF_SEGMENT_TYPE,
  PROMPT_INFO_MONO_FIELD_NAMES,
  PROMPT_MGMT_ACTIONS,
  PROMPT_WORKSPACE_TAB
} from '../constants/AgenticPromptMgmtConstants';

import {
  normalizePromptDateField,
  comparePromptVersionStrings,
  resolvePromptVersionLabel
} from '../../../common/prompt/catalogueUtils';

export {
  normalizePromptDateField,
  mapCatalogueRow,
  normalizeCatalogueRows,
  normalizeCataloguePagination,
  filterCatalogueClientSide,
  isLatestVersion,
  comparePromptVersionStrings,
  normalizePromptVersionString,
  resolvePromptVersionLabel,
  resolveLatestVersionId
} from '../../../common/prompt/catalogueUtils';

/** True when value looks like an intl message id (e.g. agenticPromptMgmt.TITLE). */
export const isPromptI18nKey = (value) => typeof value === 'string' && /^[a-zA-Z][\w]*(\.[\w]+)+$/.test(value);

/** Resolve an i18n key via intl, or return the original string when not a key. */
export const resolvePromptI18nText = (value, intl, language) => {
  if (value == null || value === '') {
    return '';
  }
  if (isPromptI18nKey(value)) {
    return intl.formatMessage(language.getText(value));
  }
  return String(value);
};

/** Map a history API row to MST-safe shape (string dates, explicit before/after snapshots). */
const mapHistoryEntry = (entry) => {
  const newPayload = entry?.newPromptpayload || entry?.newPromptPayload || null;
  const oldPayload = entry?.oldPromptpayload || entry?.oldPromptPayload || null;
  const snapshot = newPayload || entry?.snapshot || entry?.document || null;
  const hasOldPayload = oldPayload && typeof oldPayload === 'object' && Object.keys(oldPayload).length > 0;

  return {
    action_type: entry?.action_type || entry?.actionType || '',
    edited_by: entry?.edited_by || entry?.editedBy || entry?.modifiedBy || '',
    date_edited: normalizePromptDateField(entry?.date_edited ?? entry?.dateEdited ?? entry?.updatedDate) || '',
    previousVersion: entry?.previousVersion != null ? String(entry.previousVersion) : '',
    promptVersionRef: String(entry?.promptVersionRef || entry?.version || ''),
    snapshot,
    previousSnapshot: hasOldPayload ? oldPayload : null
  };
};

export const normalizeHistoryRows = (responseData) => {
  if (!responseData) {
    return [];
  }
  const rows = Array.isArray(responseData) ? responseData : responseData?.data ?? responseData?.history ?? [];
  if (!Array.isArray(rows)) {
    return [];
  }
  return rows.map(mapHistoryEntry);
};

export const buildSavePromptPayload = (draft, sourceVersion) =>
  buildFullPromptUpdatePayload({
    sourceVersion: sourceVersion || draft?.version || draft?.promptVersionRef,
    name: draft?.name,
    description: draft?.description,
    comments: draft?.comments,
    promptStart: draft?.promptStart,
    promptEnd: draft?.promptEnd,
    isAgentic: draft?.isAgentic ?? true
  });

export const buildCreatePromptPayloadFromForm = (form) => buildCreatePromptPayload(form);

export const cloneDraftDocument = (doc) => {
  if (!doc) {
    return null;
  }
  const mapped = mapResolvedDocument(doc);
  return {
    ...mapped,
    promptStart: (mapped.promptStart || []).map((m) => ({ ...m })),
    promptEnd: (mapped.promptEnd || []).map((m) => ({ ...m }))
  };
};

export const documentsAreEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);

export const getPromptGroupContent = (document, groupName) => {
  const messages = document?.[groupName] || [];
  return messages
    .map((message) => message?.content ?? '')
    .filter(Boolean)
    .join('\n\n');
};

export const resolveNewVersionDialogConfig = (config) => {
  const detailConfig = config?.detail || {};
  const dialogConfig = detailConfig.newVersionDialog || {};
  const headerActions = detailConfig.workspace?.header?.actions || config?.workspace?.header?.actions || [];
  const saveAction = headerActions.find((action) => action.action === 'savePromptVersion') ||
    headerActions.find((action) => action.action === 'savePrompt') || {
      label: 'agenticPromptMgmt.NEW_VERSION_SAVE_LABEL'
    };

  return {
    titleSuffix: dialogConfig.titleSuffix || 'agenticPromptMgmt.NEW_VERSION_TITLE_SUFFIX',
    note: dialogConfig.note || 'agenticPromptMgmt.NEW_VERSION_NOTE',
    reviewLabel: dialogConfig.reviewLabel || 'agenticPromptMgmt.NEW_VERSION_REVIEW_LABEL',
    saveLabel: dialogConfig.saveLabel || saveAction.label || 'agenticPromptMgmt.NEW_VERSION_SAVE_LABEL',
    newVersionLabel:
      dialogConfig.newVersionLabel ||
      detailConfig.versionSidebar?.newVersionLabel ||
      'agenticPromptMgmt.NEW_VERSION_TITLE_SUFFIX'
  };
};

export const diffPromptDocuments = (prevDoc, nextDoc, diffFields = []) => {
  const diffs = {};
  diffFields.forEach((field) => {
    const prevVal = prevDoc?.[field];
    const nextVal = nextDoc?.[field];
    if (JSON.stringify(prevVal) !== JSON.stringify(nextVal)) {
      diffs[field] = { before: prevVal, after: nextVal };
    }
  });
  return diffs;
};

export const formatPromptMetaDate = (value) => {
  if (value == null || value === '') {
    return '—';
  }
  const asNumber = typeof value === 'number' ? value : Number(value);
  const date =
    Number.isFinite(asNumber) && String(asNumber) === String(value).trim() ? new Date(asNumber) : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return `${date.toLocaleString(undefined, {
    timeZone: 'UTC',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  })}`;
};

export const sortVersionsDesc = (versions) =>
  [...(versions || [])].sort((a, b) =>
    comparePromptVersionStrings(resolvePromptVersionLabel(b), resolvePromptVersionLabel(a))
  );

export const getFieldValue = (doc, fieldName) => doc?.[fieldName] ?? '';

export const createEmptyDraftDocument = () => ({
  name: '',
  description: '',
  comments: '',
  promptStart: [{ role: 'system', content: '' }],
  promptEnd: [{ role: 'user', content: '' }]
});

/** Default promptStart (system) + promptEnd (user) groups for create dialog and editor. */
export const DEFAULT_PROMPT_MESSAGE_GROUPS = Object.freeze([
  {
    name: 'promptStart',
    labelKey: 'agenticPromptMgmt.MSG_GROUP_PROMPT_START_LABEL',
    descriptionKey: 'agenticPromptMgmt.MSG_GROUP_PROMPT_START_DESC',
    defaultRole: 'system',
    allowedRoles: ['system'],
    minMessages: 1
  },
  {
    name: 'promptEnd',
    labelKey: 'agenticPromptMgmt.MSG_GROUP_PROMPT_END_LABEL',
    descriptionKey: 'agenticPromptMgmt.MSG_GROUP_PROMPT_END_DESC',
    defaultRole: 'user',
    allowedRoles: ['user'],
    minMessages: 1
  }
]);

export const resolvePromptMessageGroups = (messageGroups) =>
  DEFAULT_PROMPT_MESSAGE_GROUPS.map((defaultGroup) => {
    const override = (messageGroups || []).find((group) => group.name === defaultGroup.name);
    if (!override) {
      return defaultGroup;
    }
    return {
      ...defaultGroup,
      ...override,
      defaultRole: override.defaultRole ?? defaultGroup.defaultRole,
      allowedRoles: override.allowedRoles ?? defaultGroup.allowedRoles,
      minMessages: override.minMessages ?? defaultGroup.minMessages
    };
  });

/** Map Info tab fields from backend/workspace config; all fields are read-only. */
export const resolveInfoTabFields = (fields) => {
  const configured = Array.isArray(fields) ? fields : [];
  return configured.map((field) => ({
    ...field,
    readOnly: true
  }));
};

export const formatInfoFieldValue = (field, value) => {
  if (field.component === 'DateTime') {
    return formatPromptMetaDate(value);
  }
  if (typeof value === 'boolean') {
    return value ? 'common.YES' : 'common.NO';
  }
  if (value == null || value === '') {
    return 'common.EMPTY_VALUE';
  }
  return String(value);
};

export const isPromptInfoMonoField = (fieldName) => PROMPT_INFO_MONO_FIELD_NAMES.includes(fieldName);

export const normalizePromptWorkspaceHeaderActions = (actions = []) => {
  const saveActions = actions.filter(
    (action) =>
      action.action === PROMPT_MGMT_ACTIONS.SAVE_PROMPT || action.action === PROMPT_MGMT_ACTIONS.SAVE_PROMPT_VERSION
  );
  const otherActions = actions.filter(
    (action) =>
      action.action !== PROMPT_MGMT_ACTIONS.SAVE_PROMPT && action.action !== PROMPT_MGMT_ACTIONS.SAVE_PROMPT_VERSION
  );

  if (!saveActions.length) {
    return actions;
  }

  const saveAction = saveActions.find((action) => action.action === PROMPT_MGMT_ACTIONS.SAVE_PROMPT) || saveActions[0];

  return [
    ...otherActions,
    {
      ...saveAction,
      label: saveAction.label || 'common.SAVE',
      variant: 'contained',
      action: PROMPT_MGMT_ACTIONS.SAVE_PROMPT
    }
  ];
};

export const DEFAULT_CATALOGUE_TABLE_COLUMNS = Object.freeze([
  {
    id: 'name',
    labelKey: 'agenticPromptMgmt.CATALOGUE_COL_NAME',
    field: 'name',
    sortable: true,
    link: true,
    minWidth: 200
  },
  {
    id: 'promptRef',
    labelKey: 'agenticPromptMgmt.CATALOGUE_COL_PROMPT_ID',
    field: 'promptRef',
    sortable: true,
    width: 280,
    format: 'mono'
  },
  {
    id: 'versionCount',
    labelKey: 'agenticPromptMgmt.CATALOGUE_COL_VERSIONS',
    field: 'versionCount',
    sortable: true,
    align: 'right',
    width: 100
  },
  {
    id: 'updatedDate',
    labelKey: 'agenticPromptMgmt.CATALOGUE_COL_UPDATED_AT',
    field: 'updatedDate',
    sortable: true,
    format: 'datetime',
    width: 220
  },
  {
    id: 'modifiedBy',
    labelKey: 'agenticPromptMgmt.CATALOGUE_COL_MODIFIED_BY',
    field: 'modifiedBy',
    sortable: true,
    width: 220
  }
]);

export const mapGridColumnConfigToTableColumns = (gridColumns = []) =>
  gridColumns
    .filter((col) => col && col.hideColumn !== true && col.id)
    .map((col) => {
      const field = col.field || col.id;
      const isDateField = field === 'createdDate' || field === 'updatedDate' || col.format === 'datetime';
      return {
        id: col.id,
        field,
        label: col.label || col.id,
        width: col.columnWidth || col.width,
        minWidth: col.minWidth,
        sortable: col.sortable !== false,
        link: col.link === true || field === 'name',
        format: col.format || (isDateField ? 'datetime' : undefined),
        align: col.align || (field === 'versionCount' ? 'right' : 'left')
      };
    });

export const resolveCataloguePanelConfig = (config = {}) => {
  const catalogue = config.catalogue || {};
  const catalogueTable = config.catalogueTable || {};
  const gridColumns = Array.isArray(config.gridColumnConfig) ? config.gridColumnConfig : null;
  const tableFromGrid = gridColumns?.length ? mapGridColumnConfigToTableColumns(gridColumns) : null;
  const tableColumns = catalogue.table?.columns?.length ? catalogue.table.columns : tableFromGrid;
  const searchConfig = Array.isArray(config.searchConfig) ? config.searchConfig : null;

  return {
    ...catalogue,
    title: catalogue.title || 'agenticPromptMgmt.CATALOGUE_TITLE',
    search: catalogue.search || {},
    searchConfig,
    primaryAction: catalogueTable.primaryAction || catalogue.primaryAction,
    emptyState: catalogueTable.emptyState || catalogue.emptyState,
    catalogueTable,
    table: {
      pagination: { pageSize: 50, pageSizeOptions: [25, 50, 100] },
      defaultSort: { field: 'updatedDate', direction: 'desc' },
      ...(catalogue.table || {}),
      columns: tableColumns,
      emptyState: catalogueTable.emptyState || catalogue.table?.emptyState || catalogue.emptyState
    }
  };
};

export const resolveCatalogueTableColumns = (columns) => {
  if (Array.isArray(columns) && columns.length) {
    return columns;
  }
  return DEFAULT_CATALOGUE_TABLE_COLUMNS;
};

/** Map catalogue column config to CustomTable column shape. */
export const mapCatalogueColumnsForCustomTable = (columns) => {
  const resolved = resolveCatalogueTableColumns(columns);
  return resolved.map((col) => {
    const field = col.field || col.id;
    return {
      id: field,
      label: col.label || col.labelKey || col.id,
      columnWidth: col.columnWidth || col.width,
      columnMinWidth: col.minWidth,
      hideColumn: col.hideColumn === true,
      sort: col.sortable !== false,
      isTextTruncated: field === 'promptRef' || col.isTextTruncated === true,
      isWordBreak: field === 'promptRef' || col.isWordBreak === true
    };
  });
};

export const resolveWorkspaceConfig = (config) => config?.detail?.workspace || config?.workspace || {};

export const resolveDetailConfig = (config) => config?.detail || {};

export const resolveWorkspaceTabs = (config, { excludeVersionTab = false } = {}) => {
  const tabs = resolveWorkspaceConfig(config)?.tabs || [];
  if (!excludeVersionTab) {
    return tabs;
  }
  return tabs.filter((tab) => tab.id !== PROMPT_WORKSPACE_TAB.VERSIONS);
};

export const getCatalogueCellValue = (row, column) => {
  const field = column.field || column.id;
  const value = row?.[field];
  if (field === 'promptVersionRef' && value) {
    return column.format === 'version' ? `v${value}` : value;
  }
  if (column.format === 'datetime') {
    return formatPromptMetaDate(value);
  }
  if (column.format === 'mono') {
    const text = value == null || value === '' ? '—' : String(value);
    return text;
  }
  if (field === 'versionCount' && (value == null || value === '')) {
    return '—';
  }
  if (field === 'promptType' && !value) {
    return 'text';
  }
  if (value == null || value === '') {
    return '—';
  }
  return String(value);
};

export const CATALOGUE_SEARCH_FIELD_NAME = 'name';

/** Resolve the searchConfig text field key (defaults to {@link CATALOGUE_SEARCH_FIELD_NAME}). */
export const resolveCatalogueSearchFieldName = (searchConfig = []) => {
  const textFieldConfig = searchConfig.find((item) => item.component === 'CustomTextField');
  return textFieldConfig?.name || CATALOGUE_SEARCH_FIELD_NAME;
};

/** Catalogue API search param — empty until at least `minChars` are typed. */
export const resolveCatalogueApiSearchQuery = (rawQuery, minChars = 3) => {
  const trimmed = String(rawQuery ?? '').trim();
  return trimmed.length >= minChars ? trimmed : '';
};

/** Build initial search field values from searchConfig. */
export const buildDefaultCatalogueSearchFields = (searchConfig = []) =>
  searchConfig.reduce((acc, field) => {
    if (field.component === 'CustomTextField' && field.name) {
      acc[field.name] = field.defaultValue ?? '';
    }
    return acc;
  }, {});

export const sortCatalogueRows = (rows, sortField, sortDirection) => {
  if (!sortField) {
    return rows;
  }
  const dir = sortDirection === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const aVal = a?.[sortField];
    const bVal = b?.[sortField];
    if (sortField === 'updatedDate' || sortField === 'createdDate') {
      const aTime = new Date(aVal).getTime();
      const bTime = new Date(bVal).getTime();
      if (!Number.isNaN(aTime) && !Number.isNaN(bTime)) {
        return (aTime - bTime) * dir;
      }
    }
    if (sortField === 'versionCount' || sortField === 'promptVersionRef') {
      const aNum = Number(aVal);
      const bNum = Number(bVal);
      if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
        return (aNum - bNum) * dir;
      }
    }
    return String(aVal || '').localeCompare(String(bVal || '')) * dir;
  });
};

const parseCatalogueDate = (value) => {
  if (value == null || value === '') {
    return null;
  }
  const asNumber = typeof value === 'number' ? value : Number(value);
  const date =
    Number.isFinite(asNumber) && String(asNumber) === String(value).trim() ? new Date(asNumber) : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** Human-readable relative time for catalogue cells (e.g. "12 days ago"). */
export const formatCatalogueRelativeDate = (value) => {
  const date = parseCatalogueDate(value);
  if (!date) {
    return '';
  }
  const diffMs = Date.now() - date.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months === 1 ? '' : 's'} ago`;
  const years = Math.floor(days / 365);
  return `${years} year${years === 1 ? '' : 's'} ago`;
};

/** Freshness bucket for catalogue name-dot and footer legend (based on latest update date). */
export const getCatalogueFreshnessStatus = (value) => {
  const date = parseCatalogueDate(value);
  if (!date) {
    return 'old';
  }
  const days = Math.floor((Date.now() - date.getTime()) / 86400000);
  if (days <= 14) {
    return 'recent';
  }
  if (days <= 30) {
    return 'month';
  }
  return 'old';
};

export const getCatalogueFreshnessColor = (value, colorsByStatus) => {
  const status = getCatalogueFreshnessStatus(value);
  return colorsByStatus?.[status] || colors.promptCatalogue.freshness.old;
};

export const getEmailInitials = (email) => {
  if (!email || typeof email !== 'string') {
    return '?';
  }
  const local = email.split('@')[0] || '';
  const parts = local.split(/[._-]/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return local.slice(0, 2).toUpperCase() || '?';
};

const CATALOGUE_AVATAR_COLORS = Object.freeze(colors.promptCatalogue.avatarColors);

export const getCatalogueAvatarColor = (email) => {
  if (!email) {
    return CATALOGUE_AVATAR_COLORS[0];
  }
  let hash = 0;
  for (let i = 0; i < email.length; i += 1) {
    hash = email.charCodeAt(i) + hash * 31;
  }
  return CATALOGUE_AVATAR_COLORS[Math.abs(hash) % CATALOGUE_AVATAR_COLORS.length];
};

export const formatHistoryDiffValue = (value) => {
  if (value == null || value === '') {
    return '';
  }
  if (Array.isArray(value)) {
    return value
      .map((message) => (typeof message === 'string' ? message : message?.content ?? ''))
      .filter((content) => content !== '')
      .join('\n\n');
  }
  if (typeof value === 'object') {
    return JSON.stringify(value, null, 2);
  }
  return String(value);
};

const HISTORY_DIFF_DP_CAP = 250000;

const tokenizeHistoryDiffText = (text) => (text ? text.split(/(\s+)/).filter(Boolean) : []);

const collapseHistoryDiffOps = (ops) => {
  if (!ops.length) {
    return [];
  }
  const segments = [];
  ops.forEach((op) => {
    const last = segments[segments.length - 1];
    if (last && last.type === op.type) {
      last.text += op.text;
      return;
    }
    segments.push({ type: op.type, text: op.text });
  });
  return segments;
};

const buildHistoryDiffLcsOps = (beforeParts, afterParts) => {
  const beforeCount = beforeParts.length;
  const afterCount = afterParts.length;

  if (beforeCount * afterCount > HISTORY_DIFF_DP_CAP) {
    return null;
  }

  const dp = Array.from({ length: beforeCount + 1 }, () => new Array(afterCount + 1).fill(0));
  for (let i = beforeCount - 1; i >= 0; i -= 1) {
    for (let j = afterCount - 1; j >= 0; j -= 1) {
      if (beforeParts[i] === afterParts[j]) {
        dp[i][j] = dp[i + 1][j + 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i + 1][j], dp[i][j + 1]);
      }
    }
  }

  const beforeOps = [];
  const afterOps = [];
  let i = 0;
  let j = 0;

  while (i < beforeCount && j < afterCount) {
    if (beforeParts[i] === afterParts[j]) {
      beforeOps.push({ type: HISTORY_DIFF_SEGMENT_TYPE.EQUAL, text: beforeParts[i] });
      afterOps.push({ type: HISTORY_DIFF_SEGMENT_TYPE.EQUAL, text: afterParts[j] });
      i += 1;
      j += 1;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      beforeOps.push({ type: HISTORY_DIFF_SEGMENT_TYPE.DELETE, text: beforeParts[i] });
      i += 1;
    } else {
      afterOps.push({ type: HISTORY_DIFF_SEGMENT_TYPE.INSERT, text: afterParts[j] });
      j += 1;
    }
  }

  while (i < beforeCount) {
    beforeOps.push({ type: HISTORY_DIFF_SEGMENT_TYPE.DELETE, text: beforeParts[i] });
    i += 1;
  }
  while (j < afterCount) {
    afterOps.push({ type: HISTORY_DIFF_SEGMENT_TYPE.INSERT, text: afterParts[j] });
    j += 1;
  }

  return { beforeOps, afterOps };
};

const appendHistoryDiffBlock = (segments, type, text) => {
  if (!text) {
    return;
  }
  const last = segments[segments.length - 1];
  if (last && last.type === type) {
    last.text += text;
    return;
  }
  segments.push({ type, text });
};

const refineHistoryDiffHunk = (beforeText, afterText) => {
  const tokenOps = buildHistoryDiffLcsOps(tokenizeHistoryDiffText(beforeText), tokenizeHistoryDiffText(afterText));
  if (tokenOps) {
    return {
      beforeSegments: collapseHistoryDiffOps(tokenOps.beforeOps),
      afterSegments: collapseHistoryDiffOps(tokenOps.afterOps)
    };
  }
  return {
    beforeSegments: beforeText ? [{ type: HISTORY_DIFF_SEGMENT_TYPE.DELETE, text: beforeText }] : [],
    afterSegments: afterText ? [{ type: HISTORY_DIFF_SEGMENT_TYPE.INSERT, text: afterText }] : []
  };
};

/**
 * Line + word LCS diff for history before/after panels.
 * Unchanged text stays muted; only exact deletions/insertions are highlighted.
 */
export const computeHistoryTextDiff = (beforeValue, afterValue) => {
  const beforeText = formatHistoryDiffValue(beforeValue);
  const afterText = formatHistoryDiffValue(afterValue);

  if (!beforeText && !afterText) {
    return { beforeSegments: [], afterSegments: [] };
  }

  if (beforeText === afterText) {
    return {
      beforeSegments: [{ type: HISTORY_DIFF_SEGMENT_TYPE.EQUAL, text: beforeText }],
      afterSegments: [{ type: HISTORY_DIFF_SEGMENT_TYPE.EQUAL, text: afterText }]
    };
  }

  // Prefer token-level diff when the matrices stay small enough.
  const tokenOps = buildHistoryDiffLcsOps(tokenizeHistoryDiffText(beforeText), tokenizeHistoryDiffText(afterText));
  if (tokenOps) {
    return {
      beforeSegments: collapseHistoryDiffOps(tokenOps.beforeOps),
      afterSegments: collapseHistoryDiffOps(tokenOps.afterOps)
    };
  }

  const beforeLines = beforeText.split('\n');
  const afterLines = afterText.split('\n');
  const lineOps = buildHistoryDiffLcsOps(beforeLines, afterLines);

  if (!lineOps) {
    return {
      beforeSegments: beforeText ? [{ type: HISTORY_DIFF_SEGMENT_TYPE.DELETE, text: beforeText }] : [],
      afterSegments: afterText ? [{ type: HISTORY_DIFF_SEGMENT_TYPE.INSERT, text: afterText }] : []
    };
  }

  // For large prompts: keep equal lines, word-diff each changed hunk.
  const beforeSegments = [];
  const afterSegments = [];
  let beforeIndex = 0;
  let afterIndex = 0;

  const flushHunk = (beforeHunkLines, afterHunkLines) => {
    const refined = refineHistoryDiffHunk(beforeHunkLines.join('\n'), afterHunkLines.join('\n'));
    refined.beforeSegments.forEach((segment) => appendHistoryDiffBlock(beforeSegments, segment.type, segment.text));
    refined.afterSegments.forEach((segment) => appendHistoryDiffBlock(afterSegments, segment.type, segment.text));
  };

  while (beforeIndex < lineOps.beforeOps.length || afterIndex < lineOps.afterOps.length) {
    const beforeOp = lineOps.beforeOps[beforeIndex];
    const afterOp = lineOps.afterOps[afterIndex];

    if (beforeOp?.type === HISTORY_DIFF_SEGMENT_TYPE.EQUAL && afterOp?.type === HISTORY_DIFF_SEGMENT_TYPE.EQUAL) {
      const prefix = beforeSegments.length || afterSegments.length ? '\n' : '';
      appendHistoryDiffBlock(beforeSegments, HISTORY_DIFF_SEGMENT_TYPE.EQUAL, `${prefix}${beforeOp.text}`);
      appendHistoryDiffBlock(afterSegments, HISTORY_DIFF_SEGMENT_TYPE.EQUAL, `${prefix}${afterOp.text}`);
      beforeIndex += 1;
      afterIndex += 1;
    } else {
      const beforeHunkLines = [];
      const afterHunkLines = [];
      while (lineOps.beforeOps[beforeIndex]?.type === HISTORY_DIFF_SEGMENT_TYPE.DELETE) {
        beforeHunkLines.push(lineOps.beforeOps[beforeIndex].text);
        beforeIndex += 1;
      }
      while (lineOps.afterOps[afterIndex]?.type === HISTORY_DIFF_SEGMENT_TYPE.INSERT) {
        afterHunkLines.push(lineOps.afterOps[afterIndex].text);
        afterIndex += 1;
      }

      if (beforeSegments.length || afterSegments.length) {
        if (beforeHunkLines.length) {
          beforeHunkLines[0] = `\n${beforeHunkLines[0]}`;
        }
        if (afterHunkLines.length) {
          afterHunkLines[0] = `\n${afterHunkLines[0]}`;
        }
      }

      flushHunk(beforeHunkLines, afterHunkLines);
    }
  }

  return { beforeSegments, afterSegments };
};

export const shouldShowReviewDiffSection = (group, beforeText, afterText) => {
  if (beforeText === afterText) {
    return false;
  }
  if (group.name === 'promptEnd' && !afterText.trim()) {
    return false;
  }
  return true;
};

export const shouldShowReviewPreviewSection = (group, content) => {
  if (group.name === 'promptEnd' && !content.trim()) {
    return false;
  }
  return Boolean(content.trim());
};

export const resolveCreateMessageGroups = (config) => {
  const fields = config?.fields || [];
  const messageField = fields.find((field) => field.component === 'PromptMessageEditor');
  return resolvePromptMessageGroups(messageField?.messageGroups);
};

export const getPromptMessageEditorMinHeight = (layout, role) => {
  if (layout === 'modal') {
    return role === 'user' ? 220 : 420;
  }
  return role === 'user' ? 320 : 420;
};
